import { Button, Stack, Typography } from '@mui/material'
import { CargoType, CarrierVisitStatus, HandleOpenOrdersOption } from '@planning/app/api'
import { usePlanningStore } from '@planning/AppProvider'
import { RemoveOrFinishOpenOrders } from '@planning/pages/Order'
import { VisitDataHeaderCard } from '@planning/pages/VesselVisit/Components/atoms/VisitDataHeaderCard'
import { CloseVisitDialog } from '@planning/pages/VesselVisit/Components/molecules/CloseVisitDialog'
import { VisitTimingDialog } from '@planning/pages/VesselVisit/Components/molecules/VisitTimingDialog'
import { IRailVisitItem } from '@planning/rt-stores/railVisit/RailVisitItem'
import { orderService, railVisitService } from '@planning/services'
import { formatDateTime } from '@planning/utils'
import { useTranslate } from '@tolgee/react'
import { useMinimalsTheme } from '@tom-ui/ui'
import dayjs from 'dayjs'
import { observer } from 'mobx-react-lite'
import { FC, useCallback } from 'react'

type VisitTimingsProps = {
  railVisitItem: IRailVisitItem
  isEditDisabled?: boolean
}

export const RailVisitTimings: FC<VisitTimingsProps> = observer(props => {
  const { railVisitItem, isEditDisabled = false } = props
  const { dialogStore, appViewStore } = usePlanningStore()

  const { t } = useTranslate()
  const theme = useMinimalsTheme()

  const hasAta = !!railVisitItem?.data.ata
  const hasAtd = !!railVisitItem?.data.atd

  const hasAto = !!railVisitItem?.data.operationStart
  const hasAtc = !!railVisitItem?.data.operationEnd

  const isVisitClosed = railVisitItem?.data.status === CarrierVisitStatus.Closed
  const isVisitCancelled = railVisitItem?.data.status === CarrierVisitStatus.Cancelled
  const isGeneralCargoVisit = railVisitItem?.data.cargoType === CargoType.GeneralCargo

  const handleTimeStampChange = useCallback(
    async (
      key: 'ata' | 'atd' | 'operationStart' | 'operationEnd',
      value: string | null | undefined,
      initialValue?: string | null,
    ) => {
      let timeStampName = key.toUpperCase()
      if (key === 'operationStart') timeStampName = 'ATO'
      else if (key === 'operationEnd') timeStampName = 'ATC'

      const hasInitialValue = !!initialValue

      try {
        await railVisitService.put({
          ...railVisitItem.data,
          [key]: value,
          railTrackIds: railVisitItem.data.assignedRailTrackIds,
        })

        appViewStore.setShowAlert(
          'success',
          t('timestampUpdated', '{name} was successfully {operation}.', {
            name: timeStampName,
            operation: hasInitialValue ? t('updated', 'updated') : t('created', 'created'),
          }),
        )
      } catch (error: any) {
        appViewStore.setShowAlert(
          'error',
          t('failedToUpdateTimeStamp', '{name} could not be updated.', { name: timeStampName }),
        )
      }
    },
    [appViewStore, railVisitItem.data, t],
  )

  const openSetAtaModal = useCallback(() => {
    const initialAta = railVisitItem?.data.ata ?? railVisitItem?.data.eta
    dialogStore.openDialog(
      <VisitTimingDialog
        initialValues={{ date: dayjs(initialAta) }}
        timingType='ATA'
        onSubmit={async value => handleTimeStampChange('ata', value, railVisitItem?.data.ata)}
      />,
      false,
      { open: true, maxWidth: 'xs', slotProps: {} },
    )
  }, [dialogStore, railVisitItem?.data, handleTimeStampChange])

  const openSetAtdModal = useCallback(() => {
    const intialAtd = railVisitItem?.data.atd ?? railVisitItem?.data.etd
    dialogStore.openDialog(
      <VisitTimingDialog
        initialValues={{ date: dayjs(intialAtd) }}
        timingType='ATD'
        onSubmit={async value => handleTimeStampChange('atd', value, railVisitItem?.data.atd)}
      />,
      false,
      { open: true, maxWidth: 'xs', slotProps: {} },
    )
  }, [dialogStore, railVisitItem?.data, handleTimeStampChange])

  const openSetAtoModal = useCallback(() => {
    dialogStore.openDialog(
      <VisitTimingDialog
        initialValues={{ date: dayjs(railVisitItem?.data.operationStart) }}
        timingType='ATO'
        onSubmit={async value =>
          handleTimeStampChange('operationStart', value, railVisitItem?.data.operationStart)
        }
      />,
      false,
      { open: true, maxWidth: 'xs', slotProps: {} },
    )
  }, [dialogStore, railVisitItem?.data.operationStart, handleTimeStampChange])

  const openSetAtcModal = useCallback(() => {
    dialogStore.openDialog(
      <VisitTimingDialog
        initialValues={{ date: dayjs(railVisitItem?.data.operationEnd) }}
        timingType='ATC'
        onSubmit={async value =>
          handleTimeStampChange('operationEnd', value, railVisitItem?.data.operationEnd)
        }
      />,
      false,
      { open: true, maxWidth: 'xs', slotProps: {} },
    )
  }, [dialogStore, railVisitItem?.data.operationEnd, handleTimeStampChange])

  const openCloseVisitDialog = useCallback(() => {
    dialogStore.openDialog(
      <CloseVisitDialog
        onSubmit={async () => {
          try {
            await railVisitService.closeVisit(railVisitItem.data.id)
            appViewStore.setShowAlert('success', t('visitClosed', 'Visit closed successfully'))
            dialogStore.close()
          } catch (error: any) {
            appViewStore.setShowAlert('error', error?.message || 'Failed to close visit')
          }
        }}
      />,
      false,
      { open: true, maxWidth: 'xs', slotProps: {} },
    )
  }, [dialogStore, railVisitItem.data.id, appViewStore, t])

  const AtaContainer: FC<{ isDisabled?: boolean }> = ({ isDisabled }) => (
    <VisitDataHeaderCard
      width='185px'
      title={t('ata', 'ATA')}
      onClick={() => openSetAtaModal()}
      disabled={isVisitClosed || isVisitCancelled || isDisabled}
    >
      <Typography variant='body1'>{formatDateTime(railVisitItem?.data.ata)}</Typography>
    </VisitDataHeaderCard>
  )

  const AtdContainer: FC<{ isDisabled?: boolean }> = ({ isDisabled }) => (
    <VisitDataHeaderCard
      width='185px'
      title={t('atd', 'ATD')}
      onClick={() => openSetAtdModal()}
      disabled={isVisitClosed || isVisitCancelled || isDisabled}
    >
      <Typography variant='body1'>{formatDateTime(railVisitItem?.data.atd)}</Typography>
    </VisitDataHeaderCard>
  )

  const AtoContainer: FC<{ isDisabled?: boolean }> = ({ isDisabled }) => (
    <VisitDataHeaderCard
      width='185px'
      title={t('ato', 'ATO')}
      onClick={() => openSetAtoModal()}
      disabled={isVisitClosed || isVisitCancelled || isDisabled}
    >
      <Typography variant='body1'>{formatDateTime(railVisitItem?.data.operationStart)}</Typography>
    </VisitDataHeaderCard>
  )

  const AtcContainer: FC<{ isDisabled?: boolean }> = ({ isDisabled }) => (
    <VisitDataHeaderCard
      width='185px'
      title={t('atc', 'ATC')}
      onClick={() => openSetAtcModal()}
      disabled={isVisitClosed || isVisitCancelled || isDisabled}
    >
      <Typography variant='body1'>{formatDateTime(railVisitItem?.data.operationEnd)}</Typography>
    </VisitDataHeaderCard>
  )

  if (isEditDisabled) {
    return (
      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        spacing={theme.customSpacing.s}
        justifyContent={'flex-start'}
      >
        {hasAta ? (
          <AtaContainer isDisabled />
        ) : (
          <VisitDataHeaderCard width='185px' title={t('eta', 'ETA')} disabled={true}>
            <Typography variant='body1'>
              {railVisitItem?.data.eta ? formatDateTime(railVisitItem.data.eta) : '-'}
            </Typography>
          </VisitDataHeaderCard>
        )}
        {hasAto && <AtoContainer isDisabled />}
        {hasAtc && <AtcContainer isDisabled />}
        {hasAtd && <AtdContainer isDisabled />}
      </Stack>
    )
  }

  if (!hasAta) {
    return (
      <>
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={theme.customSpacing.s}>
          <VisitDataHeaderCard width='185px' title={t('eta', 'ETA')} disabled={true}>
            <Typography variant='body1'>
              {railVisitItem?.data.eta ? formatDateTime(railVisitItem.data.eta) : '-'}
            </Typography>
          </VisitDataHeaderCard>
        </Stack>
        <Button
          data-cy='set-ata-btn'
          variant='contained'
          color='inherit'
          onClick={() => openSetAtaModal()}
          sx={{ width: '130px' }}
          disabled={isVisitCancelled}
        >
          {t('setAta', 'Set ATA')}
        </Button>
      </>
    )
  }

  if (hasAta && !hasAto) {
    return (
      <>
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={theme.customSpacing.s}>
          <AtaContainer isDisabled />
          <VisitDataHeaderCard width='185px' title={t('etd', 'ETD')} disabled={true}>
            <Typography variant='body1'>
              {railVisitItem?.data.etd ? formatDateTime(railVisitItem.data.etd) : '-'}
            </Typography>
          </VisitDataHeaderCard>
        </Stack>
        <Button
          data-cy='set-ato-btn'
          variant='contained'
          color='inherit'
          onClick={async () => {
            try {
              await railVisitService.startOperations(railVisitItem.id)
              appViewStore.setShowAlert(
                'success',
                t('operationStarted', 'Operations were successfully started.'),
              )
            } catch (error: any) {
              appViewStore.setShowAlert(
                'error',
                error?.response?.data?.detail ?? 'Failed to start operations',
              )
            }
          }}
          sx={{ width: '130px' }}
          disabled={isVisitCancelled}
        >
          {t('startOperation', 'Start operation')}
        </Button>
      </>
    )
  }

  if (hasAta && hasAto && !hasAtc) {
    return (
      <>
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={theme.customSpacing.s}>
          <AtaContainer isDisabled />
          <AtoContainer />
          <VisitDataHeaderCard width='185px' title={t('etd', 'ETD')} disabled={true}>
            <Typography variant='body1'>
              {railVisitItem?.data.etd ? formatDateTime(railVisitItem?.data.etd) : '-'}
            </Typography>
          </VisitDataHeaderCard>
        </Stack>
        <Button
          data-cy='set-atc-btn'
          variant='contained'
          color='inherit'
          sx={{ width: '130px' }}
          onClick={async () => {
            if (railVisitItem.hasOpenOrders && !isGeneralCargoVisit) {
              dialogStore.openDialog(
                <RemoveOrFinishOpenOrders
                  visitItem={railVisitItem}
                  callback={async (handleOpenInboundOrdersOption, handleOpenOutboundOrdersOption) =>
                    await railVisitService.endOperations(
                      railVisitItem.id,
                      handleOpenInboundOrdersOption,
                      handleOpenOutboundOrdersOption,
                    )
                  }
                />,
                false,
                {
                  open: true,
                  maxWidth: 'lg',
                  slotProps: {},
                },
              )
              return
            }
            try {
              if (isGeneralCargoVisit) {
                if (railVisitItem.hasOpenOrders) {
                  await orderService.removeOrFinishOpenOrders({
                    carrierVisitId: railVisitItem.id,
                    inboundRemoveOrFinishOrderOption: HandleOpenOrdersOption.Finish,
                    outboundRemoveOrFinishOrderOption: HandleOpenOrdersOption.Finish,
                  })
                }

                await railVisitService.endOperations(
                  railVisitItem.id,
                  HandleOpenOrdersOption.Finish,
                  HandleOpenOrdersOption.Finish,
                )
              } else {
                await railVisitService.endOperations(railVisitItem.id)
              }
              appViewStore.setShowAlert(
                'success',
                t('operationEnded', 'Operations were successfully ended.'),
              )
            } catch (error: any) {
              appViewStore.setShowAlert(
                'error',
                error?.response?.data?.detail ?? 'Failed to end operations',
              )
            }
          }}
          disabled={isVisitCancelled}
        >
          {t('endOperation', 'End operation')}
        </Button>
      </>
    )
  }

  if (hasAta && hasAto && hasAtc && !hasAtd) {
    return (
      <>
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={theme.customSpacing.s}>
          <AtaContainer isDisabled />
          <AtoContainer isDisabled />
          <AtcContainer />
          <VisitDataHeaderCard width='185px' title={t('etd', 'ETD')} disabled={true}>
            <Typography variant='body1'>
              {railVisitItem?.data.etd ? formatDateTime(railVisitItem.data.etd) : '-'}
            </Typography>
          </VisitDataHeaderCard>
        </Stack>
        <Button
          data-cy='set-atd-btn'
          variant='contained'
          color='inherit'
          onClick={() => openSetAtdModal()}
          sx={{ width: '130px' }}
          disabled={isVisitCancelled}
        >
          {t('setAtd', 'Set ATD')}
        </Button>
      </>
    )
  }

  if (hasAta && hasAto && hasAtc && hasAtd) {
    return (
      <>
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={theme.customSpacing.s}>
          <AtaContainer isDisabled />
          <AtoContainer isDisabled />
          <AtcContainer isDisabled />
          <AtdContainer />
        </Stack>
        {!isVisitClosed && (
          <Stack direction={'row'}>
            <Button
              data-cy='close-visit-btn'
              variant='contained'
              color='inherit'
              onClick={() => openCloseVisitDialog()}
              sx={{ width: '130px' }}
              disabled={isVisitCancelled}
            >
              {t('closeVisit', 'Close visit')}
            </Button>
          </Stack>
        )}
      </>
    )
  }

  return <></>
})
